import { StyleRoot } from "@redotech/react-util/style";
import { Alignment } from "@redotech/redo-model/email-builder";
import { defaultStarRatingWidget } from "@redotech/redo-model/review/star-rating-widget-schema";
import { WidgetAutocheck } from "@redotech/redo-model/widget";
import StarIcon from "@redotech/redo-web/arbiter-icon/misc_star_icon.svg";
import { ReviewsRating } from "@redotech/redo-web/five-star-row/reviews-rating";
import { Align, Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { useContext } from "react";
import { createRoot } from "react-dom/client";
import { RedoShopifyRpcClientContextProvider } from "../client/redo-shopify-rpc-client";
import { log } from "../monitoring/log";
import { RedoRoot } from "../root";
import STYLE_MANAGER from "../style-manager";
import {
  StarRatingsContext,
  StarRatingsProvider,
  StarRatingWidgetContext,
} from "./context";

const initializedElements = new WeakSet<Element>();
let cachedStyles: Record<string, string> = {};

function initializeStarContainer(element: Element, autocheck: WidgetAutocheck) {
  if (initializedElements.has(element)) {
    return;
  }

  const productId = element.getAttribute("product_id");
  if (!productId) {
    log("Skipping element without product_id");
    return;
  }

  const htmlElement = element as HTMLElement;

  // Extract layout styles from current element, add more as needed
  const currentElementStyles: Record<string, string> = {};
  if (htmlElement.style.gridColumn)
    currentElementStyles["grid-column"] = htmlElement.style.gridColumn;
  if (htmlElement.style.textAlign)
    currentElementStyles["text-align"] = htmlElement.style.textAlign;
  if (htmlElement.style.gridRow)
    currentElementStyles["grid-row"] = htmlElement.style.gridRow;
  if (htmlElement.style.justifySelf)
    currentElementStyles["justify-self"] = htmlElement.style.justifySelf;
  if (htmlElement.style.alignSelf)
    currentElementStyles["align-self"] = htmlElement.style.alignSelf;

  const preservedStyles =
    Object.keys(currentElementStyles).length > 0
      ? (cachedStyles = { ...currentElementStyles })
      : { ...cachedStyles };

  if (!htmlElement.style.display || htmlElement.style.display === "none") {
    htmlElement.style.display = "unset";
  }

  const storeUrl =
    globalThis.redoStorefront?.storeUrl ??
    globalThis.Shopify?.shop ??
    globalThis.Shopify?.Checkout?.apiHost;

  const baseUrl =
    globalThis.redoStorefront?.env?.REDO_SHOPIFY_SERVER_URL ??
    process.env.REDO_SHOPIFY_SERVER_URL!;

  const root = createRoot(element);
  root.render(
    <StyleRoot container={element} styleManager={STYLE_MANAGER}>
      <RedoRoot extensionShopifyClient={undefined} storeUrl={storeUrl}>
        <RedoShopifyRpcClientContextProvider
          baseUrl={baseUrl}
          storeUrl={
            globalThis.Shopify?.shop ?? globalThis.Shopify?.Checkout?.apiHost
          }
          widgetId={globalThis.redoWidgetId}
        >
          <StarRatingsProvider autocheck={autocheck} productIds={[productId]}>
            <RedoReviewStars productId={productId} />
          </StarRatingsProvider>
        </RedoShopifyRpcClientContextProvider>
      </RedoRoot>
    </StyleRoot>,
  );

  requestAnimationFrame(() => {
    for (const [prop, value] of Object.entries(preservedStyles)) {
      htmlElement.style.setProperty(prop, value);
    }

    if (!htmlElement.style.display || htmlElement.style.display === "none") {
      htmlElement.style.display = "unset";
    }
  });

  initializedElements.add(element);
}

export function initialize(autocheck: WidgetAutocheck) {
  const elements = document.querySelectorAll("div.redo-review-stars");

  for (const element of elements) {
    initializeStarContainer(element, autocheck);
  }

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === "childList") {
        for (const node of mutation.addedNodes) {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as Element;

            if (element.matches("div.redo-review-stars")) {
              initializeStarContainer(element, autocheck);
            }

            const childStarElements = element.querySelectorAll(
              "div.redo-review-stars",
            );
            for (const childElement of childStarElements) {
              initializeStarContainer(childElement, autocheck);
            }
          }
        }
      }
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });
}

const alignmentToFlexAlign: Record<Alignment, Align> = {
  [Alignment.LEFT]: "flex-start",
  [Alignment.CENTER]: "center",
  [Alignment.RIGHT]: "flex-end",
};

function RedoReviewStars({ productId }: { productId: string }) {
  const ratings = useContext(StarRatingsContext);
  const widgetSettings = useContext(StarRatingWidgetContext);
  const rating = ratings?.[productId] ?? null;

  if (!ratings) {
    return <Text fontSize="sm">Loading ratings...</Text>;
  }

  // Use defaultStarRatingWidget when widgetSettings is null or undefined
  const config = widgetSettings || defaultStarRatingWidget;

  const iconSize = config.starSize;
  const starPadding = config.starPadding;
  const iconColors = {
    filled: config.filledStarColor,
    empty: config.emptyStarColor,
  };
  const starOutlineEnabled = config.starOutlineEnabled;
  const starOutlineColor = config.starOutlineColor;
  const starOutlineWidth = config.starOutlineWidth;
  const alignment = config.alignment;
  const showStarsWhenNoReviews = config.showStarsWhenNoReviews;

  // Don't render anything if there are no reviews and showStarsWhenNoReviews is false
  if (!rating?.numReviews && !showStarsWhenNoReviews) {
    return null;
  }

  return (
    <Flex align="center" dir="column" gap="sm" py="sm" w="full">
      <Flex
        align={alignmentToFlexAlign[alignment]}
        dir="column"
        gap="sm"
        w="full"
      >
        <Flex align="center" dir="row" gap="sm">
          <ReviewsRating
            editable={false}
            Icon={StarIcon}
            iconColors={iconColors}
            iconSize={iconSize}
            rating={rating ? rating.rating : 0}
            starOutlineColor={starOutlineColor}
            starOutlineEnabled={starOutlineEnabled}
            starOutlineWidth={starOutlineWidth}
            starPadding={starPadding}
          />
          <Text fontSize="sm">{`(${rating ? rating.numReviews : 0})`}</Text>
        </Flex>
      </Flex>
    </Flex>
  );
}
